/**
 * useEmailDashboardData
 *
 * P3 shell-phase hook for the War Room Email Dashboard.
 *
 * Mirrors useWarRoomMetrics's shape (loading/error/data/refresh) so that
 * swapping the mock adapter for a real P1-backed API later requires no
 * change to any component consuming this hook — only the internal
 * `fetchSource` implementation changes.
 *
 * DASH-EMAIL-008: the Acquisition Funnel's "Delivered" stage is now real,
 * MESSAGE-grain data from GET /api/email-delivery/dashboard/summary
 * (metrics.delivered, sourced exclusively from email_delivery_messages —
 * see email_delivery_dashboard_service.py's own canonical-source
 * docstring). This is the ONLY place that ever injects a real Delivered
 * value into the funnel; emailDashboardMockAdapter.js's own buildFunnel()
 * never supplies a numeric value for any stage (see that function's doc
 * comment) — there is no mock number left for Delivered to fall back to.
 *
 * The real fetch is independent/defensive (own try/catch, same pattern as
 * useEmailReputationData.js/useEmailDeliverabilityData.js's real-API
 * overlays): a failure here never blocks the rest of this (still
 * mock-backed) view from rendering, and — deliberately, per this
 * checkpoint's own requirement — never falls back to a fabricated
 * Delivered number either. It leaves Delivered honestly unavailable
 * (null) instead, exactly like the four stages that have no real source
 * at all.
 *
 * DASH-EMAIL-P1-IMPL-004: `/summary` now accepts the SAME `period`
 * contract GET /lpe-funnel already used (see fetchRealLpeFunnelStages'
 * own comment below) -- resolved server-side by the SAME shared boundary
 * resolver, so Delivered and the four LPE stages now share one reporting
 * interval for a given selected Period. This scopes `metrics.delivered`
 * ONLY -- every other `/summary` field remains its pre-existing all-time
 * semantics (see the backend's own docstring for exactly why); this hook
 * never surfaces those other fields on Overview regardless. `/summary`
 * still accepts no Environment/Program/Campaign query parameter (no
 * per-message environment concept exists yet, and Program/Campaign have
 * no persisted MESSAGE field — DASH-EMAIL-018 characterization) — do not
 * wire environment/program/campaign into the real fetch below to
 * manufacture false filter-responsiveness.
 *
 * DASH-EMAIL-019: `governedBroadcastId`/`governedState` are a SEPARATE,
 * real pair of filters from the pre-existing (mock-only) `program`/
 * `campaign`/`broadcast` options above — those three remain wired only to
 * getMockEmailDashboardData (fed by the fake PROGRAM_HIERARCHY catalog)
 * and must never reach the real fetch, exactly as before. The new
 * `governedBroadcastId`/`governedState` pair is sourced from the real
 * governed Broadcast catalog / the canonical 50-state+DC vocabulary (see
 * EmailOverviewView.jsx's own DASH-EMAIL-019 selector) and is the ONLY
 * thing that varies the real Delivered count. Deliberately named
 * differently from `broadcast`/no `state` option existed before, to make
 * the real/mock distinction impossible to confuse at a call site.
 *
 * DASH-EMAIL-022: `observedGhlEnvironment` is a THIRD real, governed
 * filter, added alongside `governedBroadcastId`/`governedState` above.
 * `observedGhlEnvironment` reflects only GHL discovery-source provenance
 * (DASH-EMAIL-021 characterization) — never SendGrid ingestion, delivery
 * success, Broadcast execution environment, or any reconciliation between
 * concepts.
 *
 * DASH-EMAIL-P1-IMPL-006: `observedGhlEnvironment` is now derived by
 * EmailOverviewView.jsx directly from the shared EmailFilterBar's top
 * `environment` control ("all"|"qualification"|"production" — a
 * byte-identical value space), rather than from a separate, now-retired
 * "Observed GHL Environment" selector. `environment` is still ALSO passed
 * through unchanged to the mock adapter path above for every other view —
 * this hook itself performs no aliasing; each caller decides what its own
 * `environment`/`observedGhlEnvironment` arguments mean.
 *
 * DASH-EMAIL-009: Processing Health is now real, CHECKPOINT-grain data
 * from GET /api/email-delivery/dashboard/processing-health (see
 * email_delivery_processing_health_service.py's own canonical-source
 * docstring — sourced exclusively from
 * email_delivery_discovery_checkpoints, never blended with MESSAGE/
 * RECIPIENT-grain data). This hook is the ONLY place that ever injects a
 * processingHealthSources value into Overview's returned data;
 * emailDashboardMockAdapter.js no longer builds one at all (see that
 * file's own doc comment) — there is no mock fallback for it to leak
 * through. Same independent/defensive fetch pattern as Delivered above: a
 * failure here (network error, malformed response, unrecognized status
 * value) never blocks the rest of this view from rendering, and always
 * renders as the honest "unknown" state -- never a fabricated
 * healthy/failed.
 *
 * DASH-EMAIL-025: the backend's `/processing-health` response is now a
 * `sources` LIST (one entry per real discovery-source checkpoint the
 * tenant actually has — see that endpoint's own docstring for why a
 * single flat status stopped being a safe representation once a tenant
 * could genuinely have more than one checkpoint). This hook mirrors that:
 * `fetchRealProcessingHealth` returns an ARRAY, and the hook's own return
 * key is `processingHealthSources` (plural), not the old singular
 * `processingHealth`. A response with zero sources (or a malformed/
 * errored fetch) still yields a single-element `[UNKNOWN]` array — never
 * an empty array — so the UI always has at least one card to render,
 * matching this hook's pre-existing "never show nothing" discipline.
 *
 * EMAIL-DISCOVERY-OBSERVABILITY-001: each mapped source now also carries
 * `lastAttemptAt` (most recent attempt, success or failure — distinct
 * from `lastProcessed`, the most recent SUCCESS) and `lastFailureAt`/
 * `lastFailureError` (durable evidence of the most recent failure that
 * survives a later successful sweep, unlike `last_error`/`description`
 * above which reflect only the ACTIVE status). See EmailHealthCard.jsx
 * for how these are rendered.
 *
 * DASH-EMAIL-P1-IMPL-002: the funnel's four remaining stages (Landing
 * Page Views, Quote Starts, Quote Completes, High Intent Actions) are now
 * real, EVENT-grain data from GET /api/email-delivery/dashboard/lpe-funnel
 * (see email_delivery_lpe_funnel_dashboard_service.py's own canonical-
 * source docstring — sourced exclusively from the P3-owned
 * landing_page_funnel_events ledger, never blended with MESSAGE/
 * RECIPIENT/CHECKPOINT-grain data). Same independent/defensive fetch
 * pattern as Delivered/Processing Health above: a failure here never
 * blocks the rest of this view, and never fabricates a number — a failed
 * or malformed fetch leaves all four stages at their existing
 * `missing_data` shell-phase state.
 *
 * DASH-EMAIL-P1-IMPL-004: as of this checkpoint, Delivered ALSO receives
 * `period`/`customRange` (see fetchRealSummaryStages above) -- the
 * TEMPORARY Delivered-vs-LPE reporting-period mismatch
 * DASH-EMAIL-P1-IMPL-002 section 10 disclosed is now closed: all five
 * funnel stages share the same selected-period reporting interval. The
 * `periodScope` field / cross-period conversion-badge suppression that
 * mismatch required has been removed accordingly (see
 * EmailFunnelCard.jsx's own DASH-EMAIL-P1-IMPL-004 changes) -- do not
 * reintroduce it without a real reason two adjacent stages would again
 * disagree on reporting period.
 *
 * (Historical note, DASH-EMAIL-019/022/P1-CHAR-001): before DASH-EMAIL-P1-
 * IMPL-032, `governedBroadcastId`/`governedState`/`observedGhlEnvironment`
 * were deliberately never forwarded to the LPE fetch — LPE had no
 * resolved-attribution counterpart to any of those dimensions. DASH-LPE-
 * ATTRIBUTION-P1-IMPL-016/DASH-EMAIL-P1-IMPL-027 built that counterpart;
 * this checkpoint is its first frontend consumer.
 *
 * DASH-EMAIL-P1-IMPL-032: `governedBroadcastId`/`governedAudienceId`/
 * `governedProgramId`/`governedCampaignId`/`governedState` now reach
 * BOTH real fetches (EDT `/summary` and LPE `/lpe-funnel`), each already
 * fail-closed validated server-side (see the backend's own governed-
 * dimension validators) — resolved through the governed Broadcast catalog
 * for EDT and through `landing_page_origin_attributions` for LPE,
 * NEVER through any raw claim on either domain's own receipt/message.
 * Environment maps differently per domain despite sharing one UI control:
 * EDT receives it as `observedGhlEnvironment` (actual GHL discovery
 * provenance); LPE receives the SAME raw value as `environment` (the
 * governed Broadcast's own intended environment) — two independently
 * authoritative facts, never conflated, even though both use the
 * identical "qualification"/"production" vocabulary (see
 * emailDeliveryLpeFunnelApi.js's own docstring). The mock-only `program`/
 * `campaign`/`broadcast`/`environment` (fed to `getMockEmailDashboardData`
 * for the KPI/Recent Activity shell this hook still layers real data
 * onto) remain completely separate state, never forwarded to either real
 * fetch — see fetchSource's own comment below.
 *
 * `attribution_coverage` (from the LPE response) is surfaced verbatim on
 * this hook's own return value as `attributionCoverage` -- explanatory
 * metadata only, never a KPI, never a derived "fully attributed" figure.
 * Defaults to null (never a fabricated all-zero object) when the LPE
 * fetch fails or the field is absent, so a consumer can distinguish
 * "coverage unknown" from "coverage is genuinely all-zero."
 *
 * EMAIL-DASHBOARD-KPI-001: the five Executive Summary KPIs (`kpis`) are now
 * real too, built from these exact same processedCount/deliveredCount/
 * lpeStages values (never a duplicate request) -- see buildRealKpis' own
 * doc comment below for the exact five calculations, and
 * EmailOverviewView.jsx for how availability/methodology surface in the
 * UI. emailDashboardMockAdapter.js's own buildKpis() (all-UNAVAILABLE) is
 * now only ever the pre-fetch/failure shell, exactly like buildFunnel()
 * already was for the funnel stages above.
 *
 * RECENT-BROADCAST-PERFORMANCE-001: `recentBroadcastPerformance` is now
 * real, canonical-Broadcast-attributed data from GET /api/email-delivery/
 * dashboard/recent-broadcast-performance -- ONE ROW PER GOVERNED
 * BROADCAST (never an individual recipient/delivery-event/LPE-event row),
 * sorted `activity_at` descending. Each item's five metrics reuse the
 * SAME real Delivered/LPE facts this hook already fetches for the funnel
 * above (Delivered from `/summary`'s own MESSAGE-grain contract; Landing
 * Page Views/Quote Starts/Quote Completes/High Intent Actions from
 * `/lpe-funnel`'s own contract), computed server-side, never re-derived
 * here. Same independent/defensive fetch pattern as Delivered/LPE/
 * Processing Health: a failure never blocks the rest of this view, and
 * `unavailable: true` (never a fabricated row) distinguishes a genuine
 * fetch failure from an honest zero-eligible-Broadcasts result -- see
 * EMPTY_RECENT_BROADCAST_PERFORMANCE's own comment below.
 */

import { useState, useEffect, useCallback } from "react";
import { getMockEmailDashboardData } from "../lib/emailDashboardMockAdapter";
import { getEmailDeliveryDashboardSummary } from "../lib/emailDeliveryDashboardApi";
import { getEmailDeliveryProcessingHealth } from "../lib/emailDeliveryProcessingHealthApi";
import { getEmailDeliveryLpeFunnelSummary } from "../lib/emailDeliveryLpeFunnelApi";
import { getEmailDeliveryRecentBroadcastPerformance } from "../lib/emailDeliveryRecentBroadcastPerformanceApi";

const DELIVERED_STAGE_LABEL = "Delivered";
const PROCESSED_STAGE_LABEL = "Processed";

// EMAIL-DASHBOARD-KPI-001: Executive Summary ratio-KPI methodology text --
// approved verbatim, surfaced via each KPI's own info-icon tooltip (see
// EmailOverviewView.jsx). Quote Completion Rate intentionally has none: it
// is the one genuine same-grain conversion rate among the five (Quote
// Completes/Quote Starts share the LPE distinct-identity grain), so it
// carries no analytical-grain caveat.
const DELIVERY_RATE_METHODOLOGY =
  "Delivered messages divided by processed messages for the selected period. Processed and Delivered use their respective event timestamps, so this operational ratio may occasionally exceed 100% across period boundaries.";
const LANDING_PAGE_VIEW_RATE_METHODOLOGY =
  "Distinct landing-page journeys divided by delivered email messages for the selected period. This is a cross-grain engagement ratio, not a same-population conversion rate.";
const HIGH_INTENT_ACTIONS_PER_QUOTE_COMPLETE_METHODOLOGY =
  "Average number of qualifying High Intent action events per completed quote for the selected period. One prospect may generate multiple High Intent Actions.";

// EMAIL-DASHBOARD-KPI-001: shared ratio-KPI builder for every Executive
// Summary ratio card, reusing whichever real facts fetchSource already
// fetched for the Funnel above -- never a new request. Returns
// availability: "UNAVAILABLE" (never NaN, Infinity, or a fabricated
// 0%/0.0) whenever either fact failed to load (non-numeric) or the
// denominator is exactly zero -- a ratio has no defined value in either
// case. A genuine zero NUMERATOR over a real positive denominator is
// preserved as an honest 0, never upgraded to UNAVAILABLE. Never clamps
// the resulting ratio (a >100%/> 1.0 result is left exactly as computed --
// see DELIVERY_RATE_METHODOLOGY's own disclosed timing-asymmetry caveat)
// and never deduplicates either input -- both arrive here exactly as the
// real fetch produced them.
function buildRatioKpi({ numerator, denominator, denominatorLabel, methodology }) {
  if (typeof numerator !== "number" || typeof denominator !== "number") {
    return {
      value: undefined,
      availability: "UNAVAILABLE",
      reason: "Required source data is not available for the selected period.",
      methodology,
    };
  }
  if (denominator === 0) {
    return {
      value: undefined,
      availability: "UNAVAILABLE",
      reason: `${denominatorLabel} was zero for the selected period, so this ratio cannot be computed.`,
      methodology,
    };
  }
  return { value: numerator / denominator, methodology };
}

function buildCountKpi(value, label) {
  if (typeof value !== "number") {
    return {
      value: undefined,
      availability: "UNAVAILABLE",
      reason: `${label} is not available for the selected period.`,
    };
  }
  return { value };
}

// EMAIL-DASHBOARD-KPI-001: builds all five real Executive Summary KPI
// entries from the SAME already-fetched processedCount/deliveredCount/
// lpeStages fetchSource computes for the Funnel -- no duplicate request.
// `high_intent_actions_per_quote_complete` is intentionally NOT percent-
// formatted (see EmailOverviewView.jsx's own KPI_DEFS "number" format for
// this key) and is rounded to one decimal place here ONLY (never for the
// percent-formatted ratios above, which must keep full precision for an
// accurate rounded-percent display) -- "11 High Intent Actions / 5 Quote
// Completes -> 2.2", never "220%".
function buildRealKpis({ processedCount, deliveredCount, lpeStages }) {
  const landingPageViews = lpeStages["Landing Page Views"];
  const quoteStarts = lpeStages["Quote Starts"];
  const quoteCompletes = lpeStages["Quote Completes"];
  const highIntentActions = lpeStages["High Intent Actions"];

  const highIntentActionsPerQuoteComplete = buildRatioKpi({
    numerator: highIntentActions,
    denominator: quoteCompletes,
    denominatorLabel: "Quote Completes",
    methodology: HIGH_INTENT_ACTIONS_PER_QUOTE_COMPLETE_METHODOLOGY,
  });
  if (typeof highIntentActionsPerQuoteComplete.value === "number") {
    highIntentActionsPerQuoteComplete.value = Math.round(highIntentActionsPerQuoteComplete.value * 10) / 10;
  }

  return {
    emails_processed: buildCountKpi(processedCount, "Processed"),
    delivery_rate: buildRatioKpi({
      numerator: deliveredCount,
      denominator: processedCount,
      denominatorLabel: "Processed",
      methodology: DELIVERY_RATE_METHODOLOGY,
    }),
    landing_page_view_rate: buildRatioKpi({
      numerator: landingPageViews,
      denominator: deliveredCount,
      denominatorLabel: "Delivered",
      methodology: LANDING_PAGE_VIEW_RATE_METHODOLOGY,
    }),
    quote_completion_rate: buildRatioKpi({
      numerator: quoteCompletes,
      denominator: quoteStarts,
      denominatorLabel: "Quote Starts",
    }),
    high_intent_actions_per_quote_complete: highIntentActionsPerQuoteComplete,
  };
}

// DASH-EMAIL-P1-IMPL-002: maps each LPE-backed funnel stage's existing
// mock label (emailDashboardMockAdapter.js's own FUNNEL_LABELS, the only
// shared vocabulary between the mock shell and this hook's real overlay)
// to the response key GET /lpe-funnel returns it under.
const LPE_STAGE_LABEL_TO_KEY = {
  "Landing Page Views": "landing_page_views",
  "Quote Starts": "quote_starts",
  "Quote Completes": "quote_completes",
  "High Intent Actions": "high_intent_actions",
};

// DASH-EMAIL-P1-HIGHINTENT-001 Slice 4: returns BOTH `delivered` and
// `processed` from the SAME single GET /summary call (same governed
// filters, same period contract) -- deliberately not two independent
// fetches to the identical endpoint/params, which would double the
// network cost of this hook's own load for no benefit (both fields fail
// or succeed together, since they share one HTTP response). Renamed from
// this function's prior "delivered only" name/shape now that it returns
// both; every existing caller has been updated at the same time.
async function fetchRealSummaryStages(
  period,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  observedGhlEnvironment
) {
  try {
    const summary = await getEmailDeliveryDashboardSummary({
      period,
      startDate: period === "custom" ? customRange?.startDate : undefined,
      endDate: period === "custom" ? customRange?.endDate : undefined,
      broadcastId: governedBroadcastId || undefined,
      audienceId: governedAudienceId || undefined,
      programId: governedProgramId || undefined,
      campaignId: governedCampaignId || undefined,
      state: governedState || undefined,
      observedGhlEnvironment: observedGhlEnvironment || undefined,
    });
    const delivered = summary?.metrics?.delivered;
    const processed = summary?.metrics?.processed;
    return {
      delivered: typeof delivered === "number" ? delivered : null,
      processed: typeof processed === "number" ? processed : null,
    };
  } catch (err) {
    // Defensive, independent of the mock fetch below -- never blocks the
    // rest of this view from rendering. Deliberately does NOT fall back
    // to a fabricated Delivered/Processed number; both funnel stages
    // simply stay unavailable, same as a genuinely unsupported stage.
    return { delivered: null, processed: null };
  }
}

// DASH-EMAIL-P1-IMPL-002: real LPE funnel stage values, keyed by their
// existing mock label so fetchSource's funnel-mapping step below can
// merge them in without needing to know about backend response keys.
// `null` for a stage means "unavailable" (fetch failed, or malformed
// response) -- never a fabricated 0; a genuine, backend-confirmed zero is
// preserved as an honest 0.
//
// DASH-EMAIL-P1-IMPL-032: also returns `attributionCoverage` (raw
// `attribution_coverage` from the response, or `null` on failure/absence
// -- explanatory metadata only, never derived into a KPI here).
async function fetchRealLpeFunnelStages(
  period,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  lpeEnvironment
) {
  try {
    const response = await getEmailDeliveryLpeFunnelSummary({
      period,
      startDate: period === "custom" ? customRange?.startDate : undefined,
      endDate: period === "custom" ? customRange?.endDate : undefined,
      broadcastId: governedBroadcastId || undefined,
      audienceId: governedAudienceId || undefined,
      programId: governedProgramId || undefined,
      campaignId: governedCampaignId || undefined,
      state: governedState || undefined,
      environment: lpeEnvironment || undefined,
    });
    const values = {};
    for (const [label, key] of Object.entries(LPE_STAGE_LABEL_TO_KEY)) {
      const stage = response?.[key];
      values[label] = typeof stage?.value === "number" ? stage.value : null;
    }
    return {
      values,
      attributionCoverage: response?.attribution_coverage ?? null,
      highIntentBreakdown: extractHighIntentBreakdown(response?.high_intent_actions?.breakdown),
    };
  } catch (err) {
    // Defensive, independent of the mock/Delivered/Processing Health
    // fetches -- never blocks the rest of this view from rendering.
    // Deliberately does NOT fall back to a fabricated value for any of
    // the four stages; each simply stays unavailable. attributionCoverage
    // stays null (unknown), never a fabricated all-zero object.
    const values = {};
    for (const label of Object.keys(LPE_STAGE_LABEL_TO_KEY)) {
      values[label] = null;
    }
    return { values, attributionCoverage: null, highIntentBreakdown: null };
  }
}

// DASH-EMAIL-P1-HIGHINTENT-001 Slice 4: the six mutually exhaustive High
// Intent drilldown buckets, keyed by the SAME backend field names GET
// /lpe-funnel's `high_intent_actions.breakdown` object returns them
// under -- see EmailFunnelCard.jsx for the customer-facing label each
// key maps to. `null` (never a fabricated all-zero object) when the
// backend field is absent/malformed -- a consumer must not render a
// zeroed breakdown as if it were real data.
const HIGH_INTENT_BREAKDOWN_KEYS = [
  "online_enrollment_actions",
  "continue_online_actions",
  "speak_to_an_agent_actions",
  "call_agent_actions",
  "schedule_agent_actions",
  "save_quote_submissions",
];

function extractHighIntentBreakdown(breakdown) {
  if (!breakdown || typeof breakdown !== "object") return null;
  const result = {};
  for (const key of HIGH_INTENT_BREAKDOWN_KEYS) {
    if (typeof breakdown[key] !== "number") return null;
    result[key] = breakdown[key];
  }
  return result;
}

// RECENT-BROADCAST-PERFORMANCE-001: "Recent Broadcast Performance" -- ONE
// ROW PER GOVERNED BROADCAST, real canonical-Broadcast-attributed data
// from GET /api/email-delivery/dashboard/recent-broadcast-performance.
// Same independent/defensive fetch pattern as Delivered/LPE above: a
// failure here never blocks the rest of this view, and never fabricates
// rows. `unavailable: true` (never a fabricated row) distinguishes "the
// fetch itself failed" from a genuine "zero eligible Broadcasts for this
// Period/filter selection" -- EmailRecentActivityCard.jsx renders these as
// two different honest messages, never the same "no data" copy for both.
const EMPTY_RECENT_BROADCAST_PERFORMANCE = Object.freeze({
  items: [],
  totalBroadcasts: 0,
  displayedBroadcasts: 0,
  hasMore: false,
  unavailable: false,
});

function mapRecentBroadcastPerformanceItem(item) {
  return {
    broadcastId: item?.broadcast_id ?? null,
    campaignId: item?.campaign_id ?? null,
    programId: item?.program_id ?? null,
    environment: item?.environment ?? null,
    activityAt: item?.activity_at ?? null,
    delivered: typeof item?.delivered === "number" ? item.delivered : 0,
    landingPageViews: typeof item?.landing_page_views === "number" ? item.landing_page_views : 0,
    quoteStarts: typeof item?.quote_starts === "number" ? item.quote_starts : 0,
    quoteCompletes: typeof item?.quote_completes === "number" ? item.quote_completes : 0,
    highIntentActions: typeof item?.high_intent_actions === "number" ? item.high_intent_actions : 0,
  };
}

async function fetchRealRecentBroadcastPerformance(
  period,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  observedGhlEnvironment,
  lpeEnvironment
) {
  try {
    const response = await getEmailDeliveryRecentBroadcastPerformance({
      period,
      startDate: period === "custom" ? customRange?.startDate : undefined,
      endDate: period === "custom" ? customRange?.endDate : undefined,
      broadcastId: governedBroadcastId || undefined,
      audienceId: governedAudienceId || undefined,
      programId: governedProgramId || undefined,
      campaignId: governedCampaignId || undefined,
      state: governedState || undefined,
      observedGhlEnvironment: observedGhlEnvironment || undefined,
      environment: lpeEnvironment || undefined,
    });
    const items = Array.isArray(response?.items) ? response.items.map(mapRecentBroadcastPerformanceItem) : [];
    return {
      items,
      totalBroadcasts: typeof response?.total_broadcasts === "number" ? response.total_broadcasts : items.length,
      displayedBroadcasts:
        typeof response?.displayed_broadcasts === "number" ? response.displayed_broadcasts : items.length,
      hasMore: Boolean(response?.has_more),
      unavailable: false,
    };
  } catch (err) {
    return { ...EMPTY_RECENT_BROADCAST_PERFORMANCE, unavailable: true };
  }
}

// DASH-EMAIL-009: the backend's uppercase status enum
// (HEALTHY|FAILED|UNKNOWN) maps to EmailHealthCard.jsx's own lowercase
// STATE_CONFIG keys -- never passed through verbatim, and never any other
// key (watch/critical are legacy states this checkpoint's real telemetry
// never produces).
const PROCESSING_HEALTH_STATUS_MAP = {
  HEALTHY: "healthy",
  FAILED: "failed",
  UNKNOWN: "unknown",
};

// EMAIL-DISCOVERY-OBSERVABILITY-001: `lastAttemptAt` distinguishes "the
// most recent attempt" from `lastProcessed` ("the most recent SUCCESS")
// -- both may now legitimately differ when the current attempt failed.
// `lastFailureAt`/`lastFailureError` are durable evidence of the most
// recent failure that survives a later successful sweep (the backend's
// `last_error` above is still the ACTIVE-state field, cleared on
// recovery) -- these two are never cleared by recovery, so EmailHealthCard
// can show a low-emphasis "recovered from an earlier failure" note even
// once status is back to healthy.
const UNKNOWN_PROCESSING_HEALTH = Object.freeze({
  ghlLocationId: null,
  environment: null,
  status: "unknown",
  lastProcessed: null,
  lastAttemptAt: null,
  lastFailureAt: null,
  lastFailureError: null,
  description: "Processing health could not be determined from available telemetry.",
});

function describeProcessingHealth(status, lastError) {
  if (status === "failed") {
    return lastError
      ? `Email discovery is currently failing: ${lastError}`
      : "Email discovery is currently failing.";
  }
  if (status === "healthy") {
    return "Email discovery is processing normally.";
  }
  return UNKNOWN_PROCESSING_HEALTH.description;
}

// DASH-EMAIL-025: maps one backend `sources[]` entry
// ({ghl_location_id, environment, status, last_success_at, last_error})
// into this hook's own per-entry shape. Malformed/unrecognized `status`
// values still fall back to the honest "unknown" state -- never a
// fabricated healthy/failed -- but retain whatever real
// ghl_location_id/environment the entry did carry, so a partially-
// malformed entry doesn't lose its own identity.
function mapProcessingHealthSource(source) {
  const status = PROCESSING_HEALTH_STATUS_MAP[source?.status];
  const ghlLocationId = source?.ghl_location_id ?? null;
  const environment = source?.environment ?? null;
  const lastAttemptAt = source?.last_attempt_at ?? null;
  const lastFailureAt = source?.last_failure_at ?? null;
  const lastFailureError = source?.last_failure_error ?? null;
  if (!status) {
    return { ...UNKNOWN_PROCESSING_HEALTH, ghlLocationId, environment, lastAttemptAt, lastFailureAt, lastFailureError };
  }
  return {
    ghlLocationId,
    environment,
    status,
    lastProcessed: source?.last_success_at ?? null,
    lastAttemptAt,
    lastFailureAt,
    lastFailureError,
    description: describeProcessingHealth(status, source?.last_error),
  };
}

async function fetchRealProcessingHealth() {
  try {
    const response = await getEmailDeliveryProcessingHealth();
    const sources = Array.isArray(response?.sources) ? response.sources : [];
    if (sources.length === 0) {
      // Zero real checkpoints for this tenant (or a malformed response
      // shape) -- never render nothing; the UI always gets at least one
      // honest "unknown" card, matching this hook's pre-existing
      // never-show-nothing discipline.
      return [{ ...UNKNOWN_PROCESSING_HEALTH }];
    }
    return sources.map(mapProcessingHealthSource);
  } catch (err) {
    // Defensive, independent of the mock/Delivered fetches -- never blocks
    // the rest of this view from rendering, never fabricates healthy/failed.
    return [{ ...UNKNOWN_PROCESSING_HEALTH }];
  }
}

// P3 shell phase: backed by the mock adapter for every section except the
// funnel's Delivered stage above. When P1's Email read model exists for
// the remaining sections, replace the rest of this function body — the
// hook's return shape and every consumer stay unchanged.
//
// Checkpoint 3.4E: accepts program/campaign/broadcast (Overview is now
// filterable by the same Broadcast catalog Performance uses) and
// customRange (only meaningful when period === "custom") — see
// emailDashboardMockAdapter.js's own doc comment for the full contract.
// These are still passed to the MOCK fetch only (see this file's own
// header comment for why the real Delivered fetch never receives them).
const fetchSource = async ({
  period,
  environment,
  customRange,
  program,
  campaign,
  broadcast,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  observedGhlEnvironment,
  lpeEnvironment,
}) => {
  const [mockData, summaryStages, processingHealthSources, lpeResult, recentBroadcastPerformance] =
    await Promise.all([
      getMockEmailDashboardData({ period, environment, customRange, program, campaign, broadcast }),
      fetchRealSummaryStages(
        period,
        customRange,
        governedBroadcastId,
        governedAudienceId,
        governedProgramId,
        governedCampaignId,
        governedState,
        observedGhlEnvironment
      ),
      fetchRealProcessingHealth(),
      fetchRealLpeFunnelStages(
        period,
        customRange,
        governedBroadcastId,
        governedAudienceId,
        governedProgramId,
        governedCampaignId,
        governedState,
        lpeEnvironment
      ),
      fetchRealRecentBroadcastPerformance(
        period,
        customRange,
        governedBroadcastId,
        governedAudienceId,
        governedProgramId,
        governedCampaignId,
        governedState,
        observedGhlEnvironment,
        lpeEnvironment
      ),
    ]);
  const { delivered: deliveredCount, processed: processedCount } = summaryStages;
  const { values: lpeStages, attributionCoverage, highIntentBreakdown } = lpeResult;

  const funnel = (mockData.funnel || []).map((stage) => {
    if (stage.label === DELIVERED_STAGE_LABEL) {
      return {
        ...stage,
        value: deliveredCount,
        // "exact"/"missing_data" -- the same existing Metric Confidence
        // Standard vocabulary emailDashboardMockAdapter.js's funnel
        // stages already use; never a new, invented status string.
        status: deliveredCount !== null ? "exact" : "missing_data",
      };
    }
    if (stage.label === PROCESSED_STAGE_LABEL) {
      return {
        ...stage,
        value: processedCount,
        status: processedCount !== null ? "exact" : "missing_data",
      };
    }
    if (Object.prototype.hasOwnProperty.call(LPE_STAGE_LABEL_TO_KEY, stage.label)) {
      const value = lpeStages[stage.label];
      return {
        ...stage,
        value,
        status: value !== null ? "exact" : "missing_data",
      };
    }
    // Applications Submitted / Inforce Policies (and any other stage this
    // hook doesn't recognize) pass through byte-identical to the mock
    // adapter's own placeholder shape -- value: null, status:
    // "missing_data", reason: "Tracking not connected." -- never
    // overlaid with a real or fabricated value.
    return stage;
  });

  // EMAIL-DASHBOARD-KPI-001: real Executive Summary KPIs, built from the
  // exact same processedCount/deliveredCount/lpeStages values already
  // computed above for the Funnel -- overrides mockData.kpis (which stays
  // a hardcoded all-UNAVAILABLE shell, see emailDashboardMockAdapter.js's
  // own buildKpis()) the same way funnel/processingHealthSources/
  // attributionCoverage/highIntentBreakdown already override their own
  // mock defaults.
  const kpis = buildRealKpis({ processedCount, deliveredCount, lpeStages });

  return {
    ...mockData,
    kpis,
    funnel,
    processingHealthSources,
    attributionCoverage,
    highIntentBreakdown,
    recentBroadcastPerformance,
  };
};

export function useEmailDashboardData(options = {}) {
  const {
    period = "mtd",
    environment = "all",
    customRange = null,
    program = "all",
    campaign = "all",
    broadcast = "all",
    // DASH-EMAIL-019: real, governed filters -- see fetchSource's own
    // comment for why these are deliberately separate from the mock-only
    // program/campaign/broadcast above.
    governedBroadcastId = null,
    governedState = null,
    // DASH-EMAIL-P1-IMPL-032: three more real, governed filters -- same
    // reasoning as governedBroadcastId/governedState above.
    governedAudienceId = null,
    governedProgramId = null,
    governedCampaignId = null,
    // DASH-EMAIL-022: a third real, governed filter -- see fetchSource's
    // own comment above for why this is deliberately separate from the
    // mock-only `environment` prop.
    observedGhlEnvironment = null,
    // DASH-EMAIL-P1-IMPL-032: LPE's own Environment authority -- the SAME
    // raw UI value as `observedGhlEnvironment` above, but forwarded under
    // its own name/parameter to the LPE fetch specifically (never
    // `observed_ghl_environment`, see emailDeliveryLpeFunnelApi.js's own
    // docstring for the provenance distinction this preserves).
    lpeEnvironment = null,
    enabled = true,
  } = options;

  // Initialize as loading (when enabled) so the panel renders its loading
  // skeleton on first paint instead of briefly flashing the empty state
  // before the fetch effect below has a chance to run.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Checkpoint 3.4E: like useEmailPerformanceData, this hook is now fed
  // EmailDashboardPanel's *applied* filter values only — every field
  // below changes together in one commit per Apply click (see
  // useEmailFilterState's atomic apply()), so this effect fires exactly
  // once per Apply, never once per draft edit.
  const fetchData = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchSource({
        period,
        environment,
        customRange,
        program,
        campaign,
        broadcast,
        governedBroadcastId,
        governedAudienceId,
        governedProgramId,
        governedCampaignId,
        governedState,
        observedGhlEnvironment,
        lpeEnvironment,
      });
      setData(result);
    } catch (err) {
      setError(err?.message || "Failed to load Email dashboard data");
    } finally {
      setLoading(false);
    }
  }, [
    period,
    environment,
    customRange,
    program,
    campaign,
    broadcast,
    governedBroadcastId,
    governedAudienceId,
    governedProgramId,
    governedCampaignId,
    governedState,
    observedGhlEnvironment,
    lpeEnvironment,
    enabled,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    data,
    kpis: data?.kpis || {},
    funnel: data?.funnel || [],
    // RECENT-BROADCAST-PERFORMANCE-001: `unavailable: true` (never a
    // fabricated all-zero shape used as a stand-in for missing data)
    // distinguishes "the fetch failed" from a genuine zero-eligible-
    // Broadcasts result -- see EMPTY_RECENT_BROADCAST_PERFORMANCE's own
    // comment above.
    recentBroadcastPerformance: data?.recentBroadcastPerformance || { ...EMPTY_RECENT_BROADCAST_PERFORMANCE },
    processingHealthSources: data?.processingHealthSources || [],
    // DASH-EMAIL-P1-IMPL-032: raw attribution_coverage from the LPE
    // response, verbatim -- explanatory metadata only. `null` (never a
    // fabricated all-zero object) means "unknown" (fetch failed, or not
    // yet loaded) -- a consumer must not treat null as "zero coverage."
    attributionCoverage: data?.attributionCoverage ?? null,
    // DASH-EMAIL-P1-HIGHINTENT-001 Slice 4: the six mutually exhaustive
    // High Intent drilldown bucket counts, verbatim from GET /lpe-funnel's
    // high_intent_actions.breakdown -- `null` (never a fabricated all-zero
    // object) means "unknown" (fetch failed, malformed, or not yet
    // loaded), exactly like attributionCoverage above.
    highIntentBreakdown: data?.highIntentBreakdown ?? null,
    refresh: fetchData,
  };
}

export default useEmailDashboardData;
